"use client"

import { useCallback } from "react"
import { useSearchParams, usePathname } from "next/navigation"
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    PaginationEllipsis,
} from "@/components/ui/pagination"

interface PaginationComponentProps {
    lastPage: number
    total: number
    per_page?: number
}

export default function PaginationComponent({ lastPage = 1, total = 0, per_page = 10 }: PaginationComponentProps) {
    const searchParams = useSearchParams()
    const pathname = usePathname()

    const currentPage = Number.parseInt(searchParams.get("page") || "1", 10)

    const createPageURL = useCallback(
        (pageNumber: number): string => {
            const params = new URLSearchParams(searchParams)
            params.set("page", pageNumber.toString())
            return `${pathname}?${params.toString()}`
        },
        [searchParams, pathname],
    )

    const getPageNumbers = useCallback((): (number | string)[] => {
        const pages: (number | string)[] = []

        if (lastPage <= 4) {
            // اگر 4 صفحه یا کمتر داریم، همه را نمایش بده
            for (let i = 1; i <= lastPage; i++) {
                pages.push(i)
            }
        } else {
            // صفحه قبلی
            if (currentPage > 1) {
                pages.push(currentPage - 1)
            }

            // سه نقطه اول (اگر لازم باشد)
            if (currentPage > 2) {
                pages.push("...")
            }

            // صفحه فعلی
            pages.push(currentPage)

            // صفحه بعدی
            if (currentPage < lastPage) {
                pages.push(currentPage + 1)
            }

            // سه نقطه دوم (اگر لازم باشد)
            if (currentPage < lastPage - 1) {
                pages.push("...")
            }

            // آخرین صفحه (اگر قبلاً اضافه نشده باشد)
            if (currentPage < lastPage) {
                pages.push(lastPage)
            }
        }

        return pages
    }, [currentPage, lastPage])

    const toPersianNumber = (num: number): string => {
        return num?.toLocaleString("fa-IR")
    }

    if (total < per_page) return null

    return (
        <section className="w-full md:w-[calc(100%-320px)] absolute bottom-4 md:bottom-0 text-sm">
            <Pagination className={""}>
                <PaginationContent>
                    <PaginationItem>
                        <PaginationPrevious
                            href={createPageURL(Math.max(1, currentPage - 1))}
                            aria-disabled={currentPage === 1}
                            className={`${currentPage === 1 && "opacity-20 cursor-not-allowed"}`}
                        />
                    </PaginationItem>

                    {getPageNumbers().map((pageNum, index) => (
                        <PaginationItem key={index}>
                            {pageNum === "..." ? (
                                <PaginationEllipsis />
                            ) : (
                                <PaginationLink
                                    href={createPageURL(pageNum as number)}
                                    isActive={currentPage === pageNum}
                                    className={"w-7 h-7 !rounded-md"}
                                >
                                    {toPersianNumber(pageNum as number)}
                                </PaginationLink>
                            )}
                        </PaginationItem>
                    ))}

                    <PaginationItem>
                        <PaginationNext
                            className={`${lastPage === currentPage && "opacity-20 cursor-not-allowed"}`}
                            href={createPageURL(Math.min(lastPage, currentPage + 1))}
                            aria-disabled={currentPage === lastPage}
                        />
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </section>
    )
}

